import * as React from 'react';

/**
 * Debounces a value when its state is changing for a given interval
 *
 * @param  {T} value The value that is being debounced
 * @param  {number} delay The interval in milliseconds the value should be debounced
 */
export function useDebounce<T>(value: T, delay: number): T {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

  React.useEffect(() => {
    // Set debouncedValue to value (passed in) after the specified delay
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Return a cleanup function that will be called every time
    // useEffect is re-called. useEffect will only be re-called
    // if value changes (see the inputs array below).
    // This is how we prevent debouncedValue from changing if value is
    // changed within the delay period.
    // To put it in context, if the user is typing within our app's
    // search box, we don't want the debouncedValue to update until
    // they've stopped typing for more than 500ms.
    return (): void => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
